import { ReactNode } from 'react';

import { SaleDocumentType } from '__generated__/globalTypes';
import { Button } from 'ui-kit';

import { SaleDocumentGate } from './SaleDocumentGate';
import { CONFIDENTIALITY_AGREEMENT, isDocumentAcknowledged } from './saleDocuments';
import { SECONDARY_BUTTON_LIGHT_THEME } from './styles';
import { ClickThroughDocumentState, useClickThroughDocument } from './useClickThroughDocument';

export type Acknowledgements = { doc_type: SaleDocumentType; version: string }[];

export type AccessDocumentGate = {
  gate: ReactNode | null;
  documentFailedToLoad: boolean;
};

// The gate for a document that is still outstanding, or null once its current version is
// acknowledged. A document api has not answered for stays outstanding.
function outstandingGate(
  saleId: string,
  state: ClickThroughDocumentState,
  acknowledgements: Acknowledgements,
  title: string,
): ReactNode | null {
  if (state.status === 'loading') {
    return (
      <p
        data-testid="sale-document-loading"
        className="mt-6 text-sm text-gray-400 light:text-gray-700"
      >
        Loading the {title}…
      </p>
    );
  }
  if (state.status === 'failed') {
    return (
      <div className="mt-6 flex items-center gap-3">
        <p
          data-testid="sale-document-failed"
          className="text-sm text-status-error light:text-error-muted"
        >
          Failed to load the {title}.
        </p>
        <Button
          size="small"
          type="secondary"
          label="Retry"
          data-testid="sale-document-retry"
          className={SECONDARY_BUTTON_LIGHT_THEME}
          onClick={state.retry}
        >
          Retry
        </Button>
      </div>
    );
  }
  if (isDocumentAcknowledged(acknowledgements, state.document)) {
    return null;
  }
  // key gives each document a fresh gate: CA and PEC render at the same tree position in turn,
  // the route keeps one SalePage across sales, and a new version is new text. A reused instance
  // would carry a ticked checkbox over to a document, sale or version never agreed to.
  const { doc_type: docType, version } = state.document;
  return (
    <SaleDocumentGate
      key={`${saleId}:${docType}:${version}`}
      saleId={saleId}
      document={state.document}
    />
  );
}

// Exhibit 6: the CA, then the PEC summary, before a bidder requests access -- and before each
// user of an approved company reads anything, since acknowledgements are per user.
export function useAccessDocumentGate(
  saleId: string,
  acknowledgements: Acknowledgements,
): AccessDocumentGate {
  const pecSummary = useClickThroughDocument(saleId, SaleDocumentType.PEC_SUMMARY);
  const confidentialityAgreementGate = outstandingGate(
    saleId,
    { status: 'ready', document: CONFIDENTIALITY_AGREEMENT },
    acknowledgements,
    'Confidentiality Agreement',
  );
  if (confidentialityAgreementGate) {
    return { gate: confidentialityAgreementGate, documentFailedToLoad: false };
  }
  return {
    gate: outstandingGate(
      saleId,
      pecSummary,
      acknowledgements,
      'Purchaser Eligibility Certification summary',
    ),
    documentFailedToLoad: pecSummary.status === 'failed',
  };
}

// Exhibit 6, Attachment A: an L1 bidder accepts the notice before the bid form. `required` is
// false when it does not apply, and then nothing is fetched.
export function useBidNoticeGate(
  saleId: string,
  acknowledgements: Acknowledgements,
  required: boolean,
): ReactNode | null {
  const bidNotice = useClickThroughDocument(saleId, SaleDocumentType.SUMMARY_BID_NOTICE, !required);
  if (!required) {
    return null;
  }
  return outstandingGate(
    saleId,
    bidNotice,
    acknowledgements,
    'notice on bidding on summary level information',
  );
}
